import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useInvalidateBranding } from "@/lib/use-branding";
import { useTheme } from "@/lib/use-theme";
import { useToast } from "@/lib/toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sun, Moon } from "lucide-react";

/**
 * Settings page - kept intentionally small.
 *
 * 1. Software identity (name, logo, favicon)
 * 2. Appearance (light / dark only - no color pickers, no fonts, no
 *    typography presets). Brand colors come from the active vertical
 *    template, see /templates.
 * 3. Technical defaults (timezone, currency, country, default visit
 *    agent).
 */
export function SettingsPage() {
  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500">
          Identity, light / dark mode, and technical defaults. Colors and
          persona come from the active <strong>vertical template</strong>{" "}
          (see <a href="/templates" className="underline">Templates</a>).
        </p>
      </header>
      <AppearancePanel />
      <BusinessPanel />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Appearance - light / dark only
// ---------------------------------------------------------------------------

function AppearancePanel() {
  const { theme, setTheme } = useTheme();
  return (
    <Card>
      <CardHeader>
        <CardTitle>Appearance</CardTitle>
        <CardDescription>
          Light or dark mode. Stored on this device only.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="inline-flex rounded-md border border-slate-200 p-1 bg-brand-surface">
          <ThemeButton
            active={theme === "light"}
            onClick={() => setTheme("light")}
            icon={<Sun className="h-4 w-4" />}
            label="Light"
          />
          <ThemeButton
            active={theme === "dark"}
            onClick={() => setTheme("dark")}
            icon={<Moon className="h-4 w-4" />}
            label="Dark"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function ThemeButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition ${
        active
          ? "bg-slate-900 text-white"
          : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Identity + technical defaults (kept from the old page; still useful).
// ---------------------------------------------------------------------------

function BusinessPanel() {
  const qc = useQueryClient();
  const invalidateBranding = useInvalidateBranding();
  const toast = useToast();
  const { data } = useQuery({
    queryKey: ["settings", "/api/settings/business"],
    queryFn: () => api.get<Record<string, unknown>>("/api/settings/business"),
  });
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const saveMut = useMutation({
    mutationFn: () => api.put("/api/settings/business", draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings", "/api/settings/business"] });
      invalidateBranding();
      const n = Object.keys(draft).length;
      setDraft({});
      toast.success("Settings saved", `${n} field${n > 1 ? "s" : ""} updated`);
    },
    onError: (e) => toast.error("Save failed", e instanceof Error ? e.message : String(e)),
  });

  const get = (k: string) => (draft[k] !== undefined ? draft[k] : data?.[k]);
  const set = (k: string, v: unknown) => setDraft({ ...draft, [k]: v });
  const dirty = Object.keys(draft).length > 0;

  if (!data) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-slate-500">Loading…</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Software identity</CardTitle>
          <CardDescription>
            The name, logo and favicon shown across the dashboard.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Software name">
            <Input
              placeholder={String(get("business_name") ?? "AI Leasing OS")}
              value={String(get("app_brand_name") ?? "")}
              onChange={(e) => set("app_brand_name", e.target.value)}
            />
          </Field>
          <Field label="Logo URL (square, ~64×64)">
            <Input
              placeholder="https://your-domain.com/logo.png"
              value={String(get("brand_logo_url") ?? "")}
              onChange={(e) => set("brand_logo_url", e.target.value)}
            />
          </Field>
          <Field label="Favicon URL">
            <Input
              placeholder="https://your-domain.com/favicon.ico"
              value={String(get("brand_favicon_url") ?? "")}
              onChange={(e) => set("brand_favicon_url", e.target.value)}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Technical defaults</CardTitle>
          <CardDescription>
            Timezone, currency and country used when normalizing prospects and ordering visits.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          <Field label="Timezone (IANA, ex: Asia/Makassar)">
            <Input value={String(get("default_timezone") ?? "")} onChange={(e) => set("default_timezone", e.target.value)} />
          </Field>
          <Field label="Currency (3-letter, ex: IDR)">
            <Input value={String(get("default_currency") ?? "")} onChange={(e) => set("default_currency", e.target.value)} />
          </Field>
          <Field label="Country (ISO, ex: ID)">
            <Input value={String(get("default_country") ?? "")} onChange={(e) => set("default_country", e.target.value)} />
          </Field>
          <Field label="Default visit agent">
            <Input value={String(get("default_visit_agent") ?? "")} onChange={(e) => set("default_visit_agent", e.target.value)} />
          </Field>
        </CardContent>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <div className="rounded-md bg-brand-surface shadow-md border border-slate-200 px-3 py-2 flex items-center gap-3">
          <span className="text-xs text-slate-500">
            {dirty ? `${Object.keys(draft).length} unsaved change(s)` : "All saved"}
          </span>
          <Button onClick={() => saveMut.mutate()} disabled={!dirty || saveMut.isPending}>
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
